import { FocusEvent, MouseEvent, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactSelect, {
  components,
  ControlProps,
  createFilter,
  FilterOptionOption,
  FormatOptionLabelMeta,
  GroupBase,
  InputActionMeta,
  InputProps,
  SingleValue,
} from "react-select";
import { FormattedMessage } from "react-intl";

import { classNames } from "helpers/commonHelpers";

import SelectClearIndicator from "./components/SelectClearIndicator";
import SelectDropdownIndicator from "./components/SelectDropdownIndicator";
import SelectOptionComponent from "./components/SelectOption";
import SelectSingleValue from "./components/SelectSingleValue";
import { useMenuPortalLayout } from "./hooks/useMenuPortalLayout";
import { useSelectStyles } from "./hooks/useSelectStyles";
import type { BaseSelectProps, SelectOption } from "./types";

import "./Select.scss";

function resolveTestId(testId: string | undefined, name: string | undefined) {
  return testId || (name ? `search-select-${name.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}` : undefined);
}

type SelectInputSelectProps = {
  startCursorAtEnd?: boolean;
  isEditingStartCursorInput?: boolean;
  onSeedCursorAtEnd?: () => void;
};

function shouldSeedStartCursorInput(selectProps: SelectInputSelectProps) {
  return Boolean(
    selectProps.startCursorAtEnd &&
      !selectProps.isEditingStartCursorInput &&
      selectProps.onSeedCursorAtEnd,
  );
}

function seedStartCursorInputOnMouseDown(
  event: MouseEvent<HTMLElement>,
  selectProps: SelectInputSelectProps,
  innerOnMouseDown?: (event: MouseEvent<HTMLElement>) => void,
) {
  innerOnMouseDown?.(event);
  if (event.defaultPrevented) return;
  if (shouldSeedStartCursorInput(selectProps)) {
    selectProps.onSeedCursorAtEnd?.();
  }
}

function placeCursorAtEnd(input: HTMLInputElement | null) {
  if (!input?.value) return;
  const len = input.value.length;
  input.setSelectionRange(len, len);
}

function mergeInputRef(
  node: HTMLInputElement | null,
  innerRef: InputProps<SelectOption, false, GroupBase<SelectOption>>["innerRef"],
) {
  if (typeof innerRef === "function") {
    innerRef(node);
  } else if (innerRef) {
    innerRef.current = node;
  }
}

function SelectInput(props: InputProps<SelectOption, false, GroupBase<SelectOption>>) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectProps = props.selectProps as SelectInputSelectProps & typeof props.selectProps;
  const { startCursorAtEnd } = selectProps;

  useLayoutEffect(() => {
    if (startCursorAtEnd) {
      placeCursorAtEnd(inputRef.current);
    }
  }, [startCursorAtEnd, props.value, props.selectProps.menuIsOpen, selectProps.isEditingStartCursorInput]);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    props.innerProps.onFocus?.(event);
    if (startCursorAtEnd) {
      requestAnimationFrame(() => placeCursorAtEnd(event.currentTarget));
    }
  };

  const handleMouseDown = (event: MouseEvent<HTMLInputElement>) => {
    seedStartCursorInputOnMouseDown(event, selectProps, props.innerProps.onMouseDown);
  };

  return (
    <components.Input
      {...props}
      innerRef={(node) => {
        inputRef.current = node;
        mergeInputRef(node, props.innerRef);
      }}
      innerProps={{ ...props.innerProps, onFocus: handleFocus, onMouseDown: handleMouseDown }}
      data-1p-ignore
      data-lpignore="true"
    />
  );
}

function SelectControl(props: ControlProps<SelectOption, false, GroupBase<SelectOption>>) {
  const selectProps = props.selectProps as SelectInputSelectProps & typeof props.selectProps;

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    seedStartCursorInputOnMouseDown(event, selectProps, props.innerProps.onMouseDown);
  };

  return (
    <components.Control
      {...props}
      innerProps={{ ...props.innerProps, onMouseDown: handleMouseDown }}
    />
  );
}

function getSelectedDisplayText(
  option: SelectOption,
  formatOptionLabel?: (
    option: SelectOption,
    meta: FormatOptionLabelMeta<SelectOption, false, GroupBase<SelectOption>>,
  ) => ReactNode,
): string {
  if (formatOptionLabel) {
    const formatted = formatOptionLabel(option, {
      context: "value",
      inputValue: "",
      selectValue: [option],
    });
    if (typeof formatted === "string") return formatted;
  }
  return option.label;
}

type BaseSelectExtendedProps = BaseSelectProps & {
  label?: string;
  required?: boolean;
  error?: { message?: string } | null;
  hasError?: boolean;
  hintText?: string;
};

export default function BaseSelect({
  testId,
  width = "100%",
  size = "md",
  menuFitContent = false,
  menuAlign = "left",
  getMenuAnchor,
  getMenuBoundary,
  onChange,
  value = null,
  name,
  options = [],
  preselect = false,
  startCursorAtEnd = true,
  className,
  label,
  required,
  error,
  hasError = false,
  hintText,
  formatOptionLabel,
  onInputChange: externalOnInputChange,
  onFocus: externalOnFocus,
  onBlur: externalOnBlur,
  onMenuOpen: externalOnMenuOpen,
  onMenuClose: externalOnMenuClose,
  styles: externalStyles,
  isSearchable = true,
  components: externalComponents,
  menuPosition: externalMenuPosition,
  filterOption: externalFilterOption,
  ...selectRest
}: BaseSelectExtendedProps) {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [cursorAtEndInput, setCursorAtEndInput] = useState<string | null>(null);
  const cursorAtEndInputRef = useRef<string | null>(null);
  const hasUserEditedStartCursorInputRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const defaultFilterOption = useRef(createFilter()).current;

  const selectedOption = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!preselect || options.length !== 1) return;
    const soleValue = options[0]?.value;
    if (!soleValue || value === soleValue) return;
    onChange?.(soleValue);
  }, [preselect, options]);

  const { menuLayout, bumpMenuLayout, needsMenuLayout } = useMenuPortalLayout({
    containerRef,
    menuIsOpen,
    menuFitContent,
    getMenuAnchor,
    getMenuBoundary,
  });

  const mergedStyles = useSelectStyles({
    menuFitContent,
    menuIsOpen,
    menuLayout,
    menuAlign,
    externalStyles,
  });

  const resolvedTestId = resolveTestId(testId, name);

  const clearCursorAtEndInput = useCallback(() => {
    hasUserEditedStartCursorInputRef.current = false;
    cursorAtEndInputRef.current = null;
    setCursorAtEndInput(null);
  }, []);

  useEffect(() => {
    if (!startCursorAtEnd || value) return;
    clearCursorAtEndInput();
  }, [value, startCursorAtEnd, clearCursorAtEndInput]);

  const seedCursorAtEndInput = useCallback(() => {
    if (!startCursorAtEnd || !selectedOption) return;
    hasUserEditedStartCursorInputRef.current = false;
    const seededText = getSelectedDisplayText(selectedOption, formatOptionLabel);
    cursorAtEndInputRef.current = seededText;
    setCursorAtEndInput(seededText);
  }, [startCursorAtEnd, selectedOption, formatOptionLabel]);

  const resolveFilterOption = useCallback(
    (option: FilterOptionOption<SelectOption>, rawInput: string) => {
      if (
        startCursorAtEnd &&
        cursorAtEndInputRef.current !== null &&
        !hasUserEditedStartCursorInputRef.current
      ) {
        return true;
      }
      if (externalFilterOption) {
        return externalFilterOption(option, rawInput);
      }
      return defaultFilterOption(option, rawInput);
    },
    [startCursorAtEnd, externalFilterOption, defaultFilterOption],
  );

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    seedCursorAtEndInput();
    externalOnFocus?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (startCursorAtEnd) {
      clearCursorAtEndInput();
    }
    externalOnBlur?.(event);
  };

  const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    if (startCursorAtEnd) {
      if (actionMeta.action === "input-change") {
        hasUserEditedStartCursorInputRef.current = true;
        cursorAtEndInputRef.current = newValue;
        setCursorAtEndInput(newValue);
      } else if (
        actionMeta.action === "set-value" ||
        actionMeta.action === "menu-close" ||
        actionMeta.action === "input-blur" ||
        actionMeta.action === "clear"
      ) {
        clearCursorAtEndInput();
      }
    }
    externalOnInputChange?.(newValue, actionMeta);
  };

  return (
    <div
      ref={containerRef}
      className={classNames("selectV2", className, {
        "selectV2--sm": size === "sm",
        "selectV2--menu-fit-content": menuFitContent,
        "selectV2--error": hasError || Boolean(error),
      })}
      style={{ width }}
    >
      {label && <label className={classNames({ required })}>{label}</label>}
      <ReactSelect
        classNamePrefix="cb-select"
        classNames={{ option: () => "text-md" }}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        openMenuOnFocus
        onMenuOpen={() => {
          if (needsMenuLayout) bumpMenuLayout();
          setMenuIsOpen(true);
          seedCursorAtEndInput();
          externalOnMenuOpen?.();
        }}
        onMenuClose={() => {
          setMenuIsOpen(false);
          externalOnMenuClose?.();
        }}
        styles={mergedStyles}
        {...selectRest}
        menuPosition={needsMenuLayout ? "fixed" : externalMenuPosition}
        isSearchable={isSearchable}
        startCursorAtEnd={startCursorAtEnd}
        isEditingStartCursorInput={cursorAtEndInput !== null}
        onSeedCursorAtEnd={seedCursorAtEndInput}
        formatOptionLabel={formatOptionLabel}
        components={{
          Option: SelectOptionComponent,
          SingleValue: SelectSingleValue,
          ClearIndicator: SelectClearIndicator,
          DropdownIndicator: SelectDropdownIndicator,
          ...externalComponents,
          Input: SelectInput,
          ...(startCursorAtEnd ? { Control: SelectControl } : {}),
        }}
        inputId={resolvedTestId}
        data-testid={resolvedTestId}
        options={options}
        {...(startCursorAtEnd && cursorAtEndInput !== null ? { inputValue: cursorAtEndInput } : {})}
        filterOption={resolveFilterOption}
        onInputChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={(option: SingleValue<SelectOption>) => {
          onChange?.(option?.value ?? null);
        }}
        value={selectedOption}
      />
      {error && (
        <div className="text-sm error-text text-error-primary">
          {error.message ? (
            error.message
          ) : (
            <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
          )}
        </div>
      )}
      {hintText && !error && <div className="text-sm hint-text">{hintText}</div>}
    </div>
  );
}
